import * as React from 'react';
import ReactModal from 'react-modal';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import Retracked from 'js/app/retracked';

import { Typography } from '@coursera/cds-core';
import { SvgClose } from '@coursera/coursera-ui/svg';

import _t from 'i18n!nls/ui';

import 'css!./__styles__/Modal';

export class ModalCloseX extends React.Component<
  {
    onRequestClose: (e: React.MouseEvent<HTMLElement>) => void;
    forwardedRef: (ref: HTMLElement | null) => void;
    ariaDescribedBy: string;
    ariaLabel: string;
    closeIconColor?: string;
  },
  {}
> {
  render() {
    // in SSR we dismiss the modal by removing the query param that shows it
    const hrefToRemoveQueryParams = '?';
    const { onRequestClose, forwardedRef, ariaDescribedBy, ariaLabel, closeIconColor } = this.props;

    return (
      <a
        className="nostyle close-x"
        onClick={onRequestClose}
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabel}
        role="button"
        href={hrefToRemoveQueryParams}
        ref={forwardedRef}
      >
        <SvgClose color={closeIconColor} />
      </a>
    );
  }
}

type Props = {
  // ReactModal-specific props
  isOpen: boolean;
  onRequestClose: () => void;
  onAfterOpen?: () => void;

  // Coursera modal props
  children: React.ReactNode;
  title?: React.ReactNode;
  allowClose?: boolean;
  size: 'default' | 'small' | 'large' | 'x-large';
  shouldCloseOnOverlayClick?: boolean;
  closeIconColor?: string;
  className?: string;
  overlayClassName?: string;
  trackingName?: string;
  data?: any;
  modalName?: string;
  role?: string;
  ariaLabeledById?: string;
  ariaDescribedById?: string;
  appElementSelector?: string; // optional element selector that react-modal mounts to
};

class Modal extends React.Component<Props, {}> {
  declare modalCloseX: HTMLElement | null | undefined;

  static contextTypes = {
    _eventData: PropTypes.object,
  };

  declare context: LegacyContextType<typeof Modal.contextTypes>;

  static defaultProps = {
    size: 'default',
    allowClose: true,
    isOpen: true,
    appElementSelector: '#rendered-content', // this is the default mount node for all coursera apps
  };

  constructor(props: Props) {
    super(props);
    // set mount node for react-modal
    ReactModal.setAppElement(props.appElementSelector as string);
  }

  componentDidMount() {
    const { data, trackingName } = this.props;
    const { _eventData } = this.context;

    Retracked.trackComponent(_eventData, data, trackingName, 'show');
  }

  onAfterOpen = () => {
    const { allowClose, onAfterOpen } = this.props;
    // Double requestAnimationFrame defers focus until after the browser has painted.
    // The first rAF runs before the next paint; the second runs after it, by which
    // time the modal is fully laid out and any competing focus logic (e.g., from
    // closing dropdowns) has settled. Without this, focus can be overridden and
    // remain on background elements (WCAG 2.2.4.3 Focus Order).
    const focusFirstFocusable = () => {
      if (allowClose && this.modalCloseX) {
        this.modalCloseX.focus({ preventScroll: true });
      }
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(focusFirstFocusable);
    });

    if (onAfterOpen) {
      onAfterOpen();
    }
  };

  onRequestClose = () => {
    const { allowClose, onRequestClose, data, trackingName } = this.props;
    const { _eventData } = this.context;

    if (allowClose) {
      Retracked.trackComponent(_eventData, data, trackingName, 'close');
      onRequestClose();
    }
  };

  onRequestCloseX = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    this.onRequestClose();
  };

  render() {
    const {
      shouldCloseOnOverlayClick,
      size,
      className,
      title,
      allowClose,
      children,
      isOpen,
      modalName,
      role,
      ariaLabeledById,
      ariaDescribedById,
      closeIconColor,
      overlayClassName,
    } = this.props;
    // We are using the class rc-UIModal to avoid conflict with bundles/phoenix/components/Modal
    const classes = classNames('rc-UIModal', size, className);
    const overlayClassNames = classNames('rc-ModalOverlay', overlayClassName);
    return (
      <ReactModal
        className={classes}
        overlayClassName={overlayClassNames}
        isOpen={isOpen}
        onAfterOpen={this.onAfterOpen}
        onRequestClose={this.onRequestClose}
        {...(modalName && {
          contentLabel: modalName,
        })}
        {...(role && { role })}
        shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
        aria={{
          ...(ariaLabeledById || modalName ? { labelledby: ariaLabeledById ?? 'headingTitle' } : {}),
          ...(ariaDescribedById ? { describedby: ariaDescribedById } : {}),
        }}
      >
        {title && (
          <Typography variant="h1semibold" component="h2" id="headingTitle" className="title flex-1">
            {title}
          </Typography>
        )}
        {allowClose && (
          <ModalCloseX
            onRequestClose={this.onRequestCloseX}
            forwardedRef={(ref) => {
              this.modalCloseX = ref;
            }}
            ariaDescribedBy="headingTitle"
            ariaLabel={_t('Close')}
            closeIconColor={closeIconColor}
          />
        )}
        {children}
      </ReactModal>
    );
  }
}

export default Modal;
